import type { DocumentInfo } from '../types/document.js';

/**
 * What a surface shows when no document is open: a blank page everyone in the
 * meeting can annotate together.
 *
 * Added for certification issue #14 (2026-09-08). The Teams mobile stage opened
 * onto nothing but "Waiting for a shared document", because a phone cannot open
 * a document itself -- the Android file picker tears the WebView down. The
 * tester filed that as "no usable meeting functionality", and they were right.
 *
 * The id is a constant, not per-user: the Yjs room is already
 * `annoteer:{meetingId}:{documentId}`, so one fixed id gives exactly one
 * whiteboard per meeting, shared by everyone in it.
 *
 * It is deliberately NEVER stored as the surface's documentInfo. That state
 * feeds the URL stamp, follow-me, presenting and share-to-stage, and none of
 * those can resolve this id -- there is nothing at /api/documents/whiteboard. A
 * surface renders `documentInfo ?? whiteboard` and leaves every other path
 * exactly as it was.
 */
export const WHITEBOARD_DOCUMENT_ID = 'whiteboard';

/** Natural size of the page, in the image-space units annotations are stored in. */
export const WHITEBOARD_SIZE = { width: 1600, height: 1000 } as const;

export function isWhiteboard(documentId: string | null | undefined): boolean {
  return documentId === WHITEBOARD_DOCUMENT_ID;
}

let cached: DocumentInfo | null = null;

/**
 * The blank page, as a document the image viewer can open. SVG so it costs a
 * few hundred bytes and needs no canvas to produce; it is served as a blob URL,
 * which the CSP's img-src already allows.
 *
 * Cached so every render hands the viewer the same bytes -- a fresh array each
 * time would look like a new image and reload it.
 */
export function whiteboardDocument(): DocumentInfo {
  if (cached) return cached;
  const { width, height } = WHITEBOARD_SIZE;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
    `viewBox="0 0 ${width} ${height}"><rect width="100%" height="100%" fill="#ffffff"/></svg>`;
  cached = {
    documentId: WHITEBOARD_DOCUMENT_ID,
    fileName: 'Whiteboard.png',
    mimeType: 'image/svg+xml',
    documentType: 'image',
    data: new TextEncoder().encode(svg),
  };
  return cached;
}
